import { Component, OnInit } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { IUnit } from "@app/interfaces/IPeripheral";
import { IVehicle } from "@app/interfaces/IVehicle";
import { ImportStrategyReference, ImportType } from "@app/main/modules/foundation/foundation.enums";
import { ICsvImportCompletionResultDTO } from "@app/main/modules/foundation/foundation.interfaces";
import { FoundationNavigationModule } from "@app/main/modules/foundation/foundation.navigation";
import { UnitsService } from "@app/main/modules/foundation/_services/unit.service";
import { NavigationService } from "@app/services/navigation.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { finalize } from "rxjs/operators";
import './unit-list.component.scss';

@UntilDestroy()
@Component({
    selector: 'unit-list',
    template: require('./unit-list.component.html')
})
export class UnitListComponent implements OnInit {

    units: IUnit[];

    tableColumns: IVTableColumn<IUnit>[];
    tableRows: IVTableRow[];

    vehicles: IVehicle[];

    loading: boolean;
    vehiclesLoading: boolean;

    showDeviceImport: boolean = false;
    importStrategyRef = ImportStrategyReference.Device;
    importType = ImportType.Unit;

    filterTabs: IFilterBarTab<IUnit>[] = [];

    constructor(
        private readonly filterBarService: FilterBarService,
        private readonly navigationService: NavigationService,
        private readonly translationService: TranslationService,
        private readonly unitService: UnitsService,
        private readonly vTableService: VTableService) {

    }

    ngOnInit(): void {
        this.loadData();
    }

    onRowClicked(row: IVTableRow): void {
        const unit: IUnit = row.data;
        this.navigationService.go(`${FoundationNavigationModule.Units}.view`, { unitSerial: unit.Serial })
    }

    addDevices(): void {
        this.showDeviceImport = true;
    }

    closeShowDeviceForm(): void {
        this.showDeviceImport = false;
    }

    onCsvModelsSaved(event: ICsvImportCompletionResultDTO) {
        this.loading = true;

        const newUnits = JSON.parse(event.CreatedModels) as IUnit[];
        this.units.push(...newUnits);

        this.tableRows = this.vTableService.createTableRows(this.units, this.tableColumns);

        this.loading = false;
    }

    //--

    private loadData(): void {
        this.loading = true;
        this.createTable();
        this.createTabs();

        this.unitService.getAllUnitsWithMappings().pipe(untilDestroyed(this), finalize(() => this.loading = false)).subscribe(units => {
            this.units = units;
            this.tableRows = this.vTableService.createTableRows(units, this.tableColumns);
            this.loading = false;
        })
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<IUnit>("Common.All", (unit) => true));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IUnit>("Common.Installed", (unit) => !!unit.vehicleMapping?.StartUtc));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IUnit>("Common.NotInstalled", (unit) => !unit.vehicleMapping?.StartUtc));
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ tKey: "FoundationHardware.Installed", sorted: true, sortDirection: SortDirection.Descending, minTableWidth: 450, sortType: SortType.Date }),
                createCellFunction: (unit) => this.vTableService.createDateCell(unit.vehicleMapping?.StartUtc)
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: (unit) => this.vTableService.createVehicleRegCell(unit.vehicleMapping?.vehicle)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "FoundationHardware.Serial", width: 150
                }),
                createCellFunction: (unit) => this.vTableService.createTableCell(unit.Serial)
            },

            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationHardware.Name" }),
                createCellFunction: (unit) => this.vTableService.createTableCell(unit.name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationHardware.IMEI", width: 200, minTableWidth: 450 }),
                createCellFunction: (unit) => this.vTableService.createTableCell(unit.Imei)
            }

        ]
    }
}
